import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Search, Plus, FileText, Pencil, Trash2, FileSpreadsheet, Download } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { money, conIVA, fechaMX } from "@/lib/format";
import ClienteModal from "@/components/ClienteModal";
import ReporteModulo from "@/components/ReporteModulo";
import FiltroSelect, { labelDe } from "@/components/FiltroSelect";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";

const ADEUDO = [{ value: "con", label: "Con saldo pendiente" }, { value: "sin", label: "Sin saldo" }];

export default function ListaClientes() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [modalMode, setModalMode] = useState("view");
  const [vendSel, setVendSel] = useState("todos");
  const [adeudoSel, setAdeudoSel] = useState("todos");
  const vendedores = Array.from(new Set(Object.values(profiles).filter((p) => (p.rol || "").toLowerCase() === "vendedor" && p.estatus === "activo").map((p) => p.full_name).concat(clientes.map((c) => profiles[c.vendedor_asignado_id]?.full_name)).filter(Boolean))).sort((a, b) => a.localeCompare(b));

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const [clientesRes, usersRes, pedidosRes] = await Promise.all([
        api.get("/clientes"), api.get("/users"), api.get("/pedidos"),
      ]);
      const profilesMap = {};
      (usersRes.data || []).forEach((p) => (profilesMap[p.id] = p));
      const saldoPorCliente = {};
      (pedidosRes.data || []).forEach((p) => {
        if (!p.cliente_id) return;
        const facturado = !!(p.factura || p.fecha_facturacion || p.estatus_pago === "Pendiente de cobro" || p.estatus === "Facturación" || p.estatus === "Vencimiento" || p.estatus === "Facturado");
        const noPagado = !(p.estatus_pago === "Pagado" || p.estatus_pago === "Cobrado" || p.fecha_pago || p.estatus === "Pago" || p.estatus === "Pedido cerrado");
        const conSaldo = Number(p.subtotal || 0) > 0;
        if (facturado && noPagado && conSaldo) saldoPorCliente[p.cliente_id] = (saldoPorCliente[p.cliente_id] || 0) + conIVA(p.subtotal);
      });
      setProfiles(profilesMap);
      setClientes((clientesRes.data || []).map((c) => ({ ...c, saldo_pendiente: saldoPorCliente[c.id] || 0 })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClientes(); }, []);
  useRefetchOnFocus(fetchClientes);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de que desea eliminar este cliente?")) return;
    try {
      await api.delete(`/clientes/${id}`);
      setClientes((prev) => prev.filter((c) => c.id !== id));
      toast.success("Cliente eliminado");
    } catch (err) {
      toast.error(`Error al eliminar: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const openModal = (cliente, mode) => { setSelectedCliente(cliente); setModalMode(mode); };

  const exportarExcel = () => {
    const cols = ["ID Cliente", "Nombre / Razón Social", "RFC", "Contacto", "Teléfono", "Email", "Municipio", "Estado", "Vendedor", "Cliente desde", "Saldo Pendiente"];
    const rows = filtered.map((c) => [
      c.id_cliente || "", c.nombre_razon_social || "", c.rfc || "", c.nombre_contacto || "",
      c.telefono || "", c.email || "", c.municipio_alcaldia || "", c.estado || "",
      profiles[c.vendedor_asignado_id]?.full_name || "", c.created_at ? fechaMX(c.created_at) : "",
      Number(c.saldo_pendiente || 0).toFixed(2),
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = "\uFEFF" + [cols, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `clientes_${format(new Date(), "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const onSave = (updated) => {
    setClientes((prev) => prev.map((c) => (c.id === updated.id ? { ...updated, saldo_pendiente: c.saldo_pendiente } : c)));
    setSelectedCliente(updated); setModalMode("view");
  };

  const filtered = clientes.filter((c) => {
    if (isAdmin && vendSel !== "todos" && (profiles[c.vendedor_asignado_id]?.full_name || "") !== vendSel) return false;
    if (adeudoSel === "con" && !(Number(c.saldo_pendiente || 0) > 1)) return false;
    if (adeudoSel === "sin" && Number(c.saldo_pendiente || 0) > 1) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    const v = profiles[c.vendedor_asignado_id];
    const vName = v?.full_name || v?.email || c.vendedor_asignado_id || "";
    const vId = v?.numero_identificacion || "";
    return (
      (c.id_cliente && c.id_cliente.toLowerCase().includes(s)) ||
      (c.nombre_razon_social && c.nombre_razon_social.toLowerCase().includes(s)) ||
      (c.rfc && c.rfc.toLowerCase().includes(s)) ||
      (c.nombre_contacto && c.nombre_contacto.toLowerCase().includes(s)) ||
      vName.toLowerCase().includes(s) || vId.toString().toLowerCase().includes(s)
    );
  });

  if ((user?.rol || "").toLowerCase() === "fabrica") {
    return <div className="flex h-64 items-center justify-center"><p className="text-red-500 font-semibold text-xl">Acceso no autorizado</p></div>;
  }

  const colSpan = isAdmin ? 9 : 7;

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="lista-clientes-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lista de clientes</h1>
          <p className="text-muted-foreground mt-2">Directorio de clientes y prospectos registrados.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportarExcel} data-testid="export-excel-btn"><Download className="mr-2 h-4 w-4" /> Exportar a Excel</Button>
            <Button onClick={() => navigate("/alta-clientes")} data-testid="nuevo-cliente-btn"><Plus className="mr-2 h-4 w-4" /> Nuevo Cliente</Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Buscar por ID, nombre, RFC, contacto, vendedor o ID usuario..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cliente-search" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {isAdmin && <FiltroSelect value={vendSel} onChange={setVendSel} todosLabel="Todos los vendedores" options={vendedores} testid="cliente-filtro-vendedor" />}
            <FiltroSelect value={adeudoSel} onChange={setAdeudoSel} todosLabel="Todos los saldos" options={ADEUDO} testid="cliente-filtro-adeudo" />
            {isAdmin && (
            <ReporteModulo
              titulo="REPORTE DE CLIENTES"
              filenameBase="Reporte clientes"
              prefix="rep-clientes"
              triggerTestId="clientes-reporte-btn"
              buttonVariant="outline"
              rows={filtered}
              getFecha={(c) => (c.created_at ? String(c.created_at).slice(0, 10) : null)}
              groupBy={(c) => profiles[c.vendedor_asignado_id]?.full_name || "Sin asignar"}
              filtros={[{ label: "Vendedor", value: labelDe(vendSel, vendedores) }, { label: "Saldo", value: labelDe(adeudoSel, ADEUDO) }, { label: "Búsqueda", value: search }]}
              columns={[
                { header: "ID Cliente", value: (c) => c.id_cliente || "-", mono: true },
                { header: "Nombre / Razón Social", value: (c) => c.nombre_razon_social || "-" },
                { header: "Municipio", value: (c) => c.municipio_alcaldia || "-" },
                { header: "Estado", value: (c) => c.estado || "-" },
                { header: "Vendedor", value: (c) => profiles[c.vendedor_asignado_id]?.full_name || "-" },
                { header: "Cliente desde", value: (c) => fechaMX(c.created_at) },
                { header: "Saldo Pendiente", align: "right", value: (c) => money(c.saldo_pendiente) },
              ]}
              subtotal={(rows) => money(rows.reduce((s, c) => s + Number(c.saldo_pendiente || 0), 0))}
              totales={(rows) => [{ label: "Total saldo pendiente", value: money(rows.reduce((s, c) => s + Number(c.saldo_pendiente || 0), 0)) }]}
            />
            )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID Cliente</TableHead>
                  <TableHead>Nombre / Razón Social</TableHead>
                  <TableHead>Municipio</TableHead>
                  <TableHead>Estado</TableHead>
                  {isAdmin && <TableHead>Vendedor</TableHead>}
                  {isAdmin && <TableHead>ID usuario</TableHead>}
                  <TableHead>Cliente desde</TableHead>
                  <TableHead className="text-right">Saldo Pendiente</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={colSpan} className="text-center h-32 text-muted-foreground">Cargando clientes...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={colSpan} className="text-center h-32 text-muted-foreground">No hay clientes registrados o que coincidan con la búsqueda.</TableCell></TableRow>
                ) : (
                  filtered.map((c) => {
                    const tieneDeuda = (c.saldo_pendiente || 0) > 1;
                    return (
                      <TableRow key={c.id} className={tieneDeuda ? "bg-red-500/10 hover:bg-red-500/20" : ""}>
                        <TableCell className="font-mono text-xs font-semibold">{c.id_cliente || "-"}</TableCell>
                        <TableCell className="font-medium">{c.nombre_razon_social}{c.credito_estatus === "pendiente_firma" && <span className="ml-2 text-[10px] font-semibold text-amber-600 border border-amber-400 rounded px-1 py-0.5 whitespace-nowrap" data-testid={`credito-pend-${c.id}`}>Crédito pend. firma</span>}</TableCell>
                        <TableCell>{c.municipio_alcaldia}</TableCell>
                        <TableCell>{c.estado}</TableCell>
                        {isAdmin && <TableCell>{profiles[c.vendedor_asignado_id]?.full_name || profiles[c.vendedor_asignado_id]?.email || c.vendedor_asignado_id || "-"}</TableCell>}
                        {isAdmin && <TableCell className="font-mono text-xs">{profiles[c.vendedor_asignado_id]?.numero_identificacion || "-"}</TableCell>}
                        <TableCell>{fechaMX(c.created_at)}</TableCell>
                        <TableCell className="text-right font-medium">{money(c.saldo_pendiente)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="icon" title="Estado de Cuenta" onClick={() => navigate(`/lista-clientes/${c.id}/estado-cuenta`)} data-testid={`estado-cuenta-${c.id}`}><FileSpreadsheet className="h-4 w-4" /></Button>
                            <Button variant="outline" size="icon" title="Ficha" onClick={() => openModal(c, "view")} data-testid={`ficha-${c.id}`}><FileText className="h-4 w-4" /></Button>
                            {isAdmin && (
                              <>
                                <Button variant="outline" size="icon" title="Editar" onClick={() => openModal(c, "edit")}><Pencil className="h-4 w-4" /></Button>
                                <Button variant="destructive" size="icon" title="Eliminar" onClick={() => handleDelete(c.id)} data-testid={`delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {selectedCliente && (
        <ClienteModal cliente={selectedCliente} mode={modalMode} setMode={setModalMode} onClose={() => setSelectedCliente(null)} onSave={onSave} isAdmin={isAdmin} />
      )}
    </div>
  );
}
